import React, { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

import { RootState } from '../../app/rootReducer';
import { ApplicationDetailedLifecycle } from '../../common/components/ApplicationDetailedLifecycle';
import { ApplicationStageV2 } from '../../common/components/ApplicationStageV2';
import { Product } from '../../common/components/Product';
import { Spacer } from '../../common/components/v2';
import { Resource } from '../../common/constants/resource';
import { isUnderwriterUWX } from '../../common/constants/roles';
import { UserOrganization } from '../../common/constants/userOrganization';
import { ExistingPolicy, PastApplication } from '../../common/dataTypes';
import { ProductFactory } from '../../common/productConfig/productFactory';
import {
  getOwnerLinkedPolicies,
  OwnerLinkedPoliciesResponse,
} from '../../common/services/ownerProfileService';
import { AuthContext } from '../login/AuthWrapper';
import styles from './styles.module.scss';

// Detail fields shown in the expanded panel for a linked policy
interface PolicyDetailFields {
  rateClass?: string;
  debits?: number;
  tobaccoClass?: string;
  product?: string;
  carrier?: string;
  subCategory?: string;
  term?: number;
  coverage?: number;
  decisionDate?: string;
}

const PolicyDetailPanel = ({ fields }: { fields: PolicyDetailFields }) => {
  const {
    rateClass,
    debits,
    tobaccoClass,
    product,
    carrier,
    subCategory,
    term,
    coverage,
    decisionDate,
  } = fields;

  const formatCoverage = (v?: number) =>
    v != null ? `$${v.toLocaleString()}` : '—';

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const cards: { label: string; value: React.ReactNode }[] = [
    { label: 'Rate class', value: rateClass ?? '—' },
    {
      label: 'Debits',
      value:
        debits != null ? (debits > 0 ? `+${debits}` : String(debits)) : '—',
    },
    { label: 'Tobacco rating', value: tobaccoClass ?? '—' },
    {
      label: 'Product',
      value: product ? (
        <Product
          product={product}
          carrier={carrier ?? ''}
          subCategory={subCategory ?? ''}
        />
      ) : (
        '—'
      ),
    },
    { label: 'Term', value: term ? `${term} yr` : '—' },
    { label: 'Coverage', value: formatCoverage(coverage) },
    { label: 'Decision date', value: formatDate(decisionDate) },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 1,
        p: 1.5,
        backgroundColor: 'background.default',
      }}
    >
      {cards.map(({ label, value }) => (
        <Box
          key={label}
          sx={{
            backgroundColor: 'background.paper',
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1.25,
            py: 1,
          }}
        >
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              mb: 0.25,
            }}
          >
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const ExistingPolicies = ({
  policyId,
  policyNumber,
  policies,
  stackedCarrierPolicyNumbers,
  stackedTermPolicyIds,
  bundledCarrierPolicyNumber,
  bundledPolicyId,
}: {
  policyId: number;
  policyNumber: string;
  policies: {
    activatedPolicies: ExistingPolicy[];
    maestroPolicies: PastApplication[];
  } | null;
  stackedCarrierPolicyNumbers: string[] | undefined;
  stackedTermPolicyIds: number[] | undefined;
  bundledCarrierPolicyNumber?: string;
  bundledPolicyId?: number;
}) => {
  const auth = useContext(AuthContext);

  // TODO: Remove this component once the new LinkedPolicies component (behind
  // isNewLinkedPoliciesSectionEnabled) fully replaces it. This flag is hardcoded off
  // because linkedPolicyDetailsEnabled will never be turned on.
  const isPolicyDetailsEnabled = false;

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (key: string) =>
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const { product, subCategory, carrier, applicationId } = useSelector(
    (state: RootState) => ({
      product: state.application.product,
      subCategory: state.application.subCategory,
      carrier: state.application.carrier,
      applicationId: state.application.applicationId,
    }),
    shallowEqual,
  );

  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory ?? '',
  );
  const isOwnerInsuredSplitVariant =
    productConfig?.shouldRenderOwnerLinkedPolicies() ?? false;

  const [ownerPoliciesData, setOwnerPoliciesData] =
    useState<OwnerLinkedPoliciesResponse | null>(null);
  useEffect(() => {
    if (isOwnerInsuredSplitVariant && applicationId) {
      getOwnerLinkedPolicies(String(applicationId)).then(setOwnerPoliciesData);
    }
  }, [isOwnerInsuredSplitVariant, applicationId]);

  // find stacked policies from actived policies list
  const stackedPolicies: ExistingPolicy[] = useMemo(() => {
    const result: ExistingPolicy[] = [];

    // Handle carrier policy numbers (existing logic)
    if (stackedCarrierPolicyNumbers && stackedCarrierPolicyNumbers.length > 0) {
      const carrierBasedPolicies = (policies?.activatedPolicies ?? []).filter(
        ({ carrierPolicyNumber }) =>
          stackedCarrierPolicyNumbers.includes(carrierPolicyNumber),
      );
      result.push(...carrierBasedPolicies);

      // Add policies not found in activated list
      stackedCarrierPolicyNumbers.forEach(carrierPolicyNumber => {
        if (
          !result.find(
            policy => policy.carrierPolicyNumber === carrierPolicyNumber,
          )
        ) {
          // add policies not found in activated list
          console.log(
            `policy ${carrierPolicyNumber} is not found in activated policies.`,
          );
          result.push({ carrierPolicyNumber } as ExistingPolicy);
        }
      });
    }

    // Handle term policy IDs (PPWB)
    if (stackedTermPolicyIds && stackedTermPolicyIds.length > 0) {
      const termBasedPolicies = (policies?.activatedPolicies ?? []).filter(
        ({ termPolicyId }) =>
          termPolicyId && stackedTermPolicyIds.includes(termPolicyId),
      );
      result.push(...termBasedPolicies);

      // Add policies not found in activated list
      stackedTermPolicyIds.forEach(termPolicyId => {
        if (!result.find(policy => policy.termPolicyId === termPolicyId)) {
          console.log(
            `policy ${termPolicyId} is not found in activated policies.`,
          );
          result.push({ termPolicyId } as ExistingPolicy);
        }
      });
    }

    return result;
  }, [policies, stackedCarrierPolicyNumbers, stackedTermPolicyIds]);

  const activatedPolicies: ExistingPolicy[] = useMemo(() => {
    return (policies?.activatedPolicies ?? []).filter(policy => {
      // Skip if this is the current policy
      if (policy.carrierPolicyNumber === policyNumber) {
        return false;
      }

      // Skip if this policy is in the stacked carrier policy numbers
      if (stackedCarrierPolicyNumbers?.includes(policy.carrierPolicyNumber)) {
        return false;
      }

      // Skip if this policy is in the stacked term policy IDs
      if (
        policy.termPolicyId &&
        stackedTermPolicyIds?.includes(policy.termPolicyId)
      ) {
        return false;
      }

      // Include this policy
      return true;
    });
  }, [
    policies,
    stackedCarrierPolicyNumbers,
    stackedTermPolicyIds,
    policyId,
    policyNumber,
  ]);

  const maestroPolicies: PastApplication[] = useMemo(() => {
    return (policies?.maestroPolicies ?? []).filter(
      p => p.policyId !== policyId,
    );
  }, [policies, policyId]);

  // Only users having UserOrganisation = ETHOS can view past applications table
  const canUserViewPastApplications = (): boolean =>
    auth.userOrganization === UserOrganization.ETHOS &&
    !isUnderwriterUWX(auth?.roles);

  const hasInsuredPolicies =
    stackedPolicies.length > 0 ||
    activatedPolicies.length > 0 ||
    maestroPolicies.length > 0;

  const hasOwnerPolicies =
    (ownerPoliciesData?.activatedPolicies?.length ?? 0) > 0 ||
    (ownerPoliciesData?.maestroPolicies?.length ?? 0) > 0;

  if (!hasInsuredPolicies && !hasOwnerPolicies) {
    return null;
  }

  const renderExpandButton = (rowKey: string) => {
    if (!isPolicyDetailsEnabled) return null;
    const isOpen = expandedRows.has(rowKey);
    return (
      <TableCell style={{ width: 40, padding: '0 4px' }}>
        <IconButton
          size="small"
          onClick={() => toggleRow(rowKey)}
          aria-label={isOpen ? 'Collapse details' : 'Expand details'}
        >
          {isOpen ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      </TableCell>
    );
  };

  const renderDetailRow = (
    rowKey: string,
    fields: PolicyDetailFields,
    colSpan: number,
  ) => {
    if (!isPolicyDetailsEnabled) return null;
    return (
      <TableRow>
        <TableCell
          colSpan={colSpan}
          style={{ padding: 0, borderBottom: 'none' }}
        >
          <Collapse in={expandedRows.has(rowKey)} unmountOnExit>
            <PolicyDetailPanel fields={fields} />
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderExistingPoliciesTable = (items: ExistingPolicy[]) => {
    if (items.length === 0) return null;
    return (
      <TableRow>
        <TableCell>
          <Typography variant="h3">
            {Resource.TASK_TITLE_EXISTING_POLICIES}
          </Typography>
          <Table>
            <TableBody>
              {items.map(
                ({
                  applicationId: policyAppId,
                  carrierPolicyNumber,
                  product: policyProduct,
                  carrier: policyCarrier,
                  subCategory: policySubCategory,
                  status,
                  coverage,
                  term: policyTerm,
                  detailedLifeCycleV2,
                  stageV2,
                  lastRelevantDecision,
                }) => {
                  const rateClass = lastRelevantDecision?.rateClass;
                  const debits = lastRelevantDecision?.debits;
                  const tobaccoClass = lastRelevantDecision?.tobaccoClass;
                  const decisionDate = lastRelevantDecision?.createdAt;
                  const isBundled =
                    bundledCarrierPolicyNumber &&
                    carrierPolicyNumber === bundledCarrierPolicyNumber;
                  const rowKey = carrierPolicyNumber ?? String(policyAppId);
                  const colSpan =
                    5 +
                    (detailedLifeCycleV2 ? 1 : 0) +
                    (stageV2 ? 1 : 0) +
                    (isPolicyDetailsEnabled ? 1 : 0);
                  return (
                    <React.Fragment key={carrierPolicyNumber}>
                      <TableRow>
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '15%' }}
                        >
                          {policyAppId ? (
                            <Link
                              component={RouterLink}
                              to={`/workbench/applications/${policyAppId}`}
                              target="_blank"
                            >
                              {carrierPolicyNumber}
                              {isBundled ? ' (bundled)' : ''}
                            </Link>
                          ) : (
                            <Typography
                              style={{
                                fontVariantNumeric: 'ordinal tabular-nums',
                              }}
                            >
                              {carrierPolicyNumber}
                              {isBundled ? ' (bundled)' : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '30%' }}
                        >
                          <Typography variant="body2">{status}</Typography>
                        </TableCell>
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '20%' }}
                        >
                          <Product
                            product={policyProduct}
                            carrier={policyCarrier}
                            subCategory={policySubCategory}
                          />
                        </TableCell>
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '15%' }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              fontVariantNumeric: 'ordinal tabular-nums',
                            }}
                          >
                            ${coverage?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '10%' }}
                        >
                          {policyTerm ? (
                            <Typography variant="body2">
                              {policyTerm} Years
                            </Typography>
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </TableCell>
                        {detailedLifeCycleV2 && (
                          <TableCell
                            className={styles.PolicyTableCell}
                            style={{ minWidth: '10%' }}
                          >
                            <ApplicationDetailedLifecycle
                              detailedLifecycle={detailedLifeCycleV2}
                            />
                          </TableCell>
                        )}
                        {stageV2 && (
                          <TableCell
                            className={styles.PolicyTableCell}
                            style={{ minWidth: '10%' }}
                          >
                            <ApplicationStageV2 stage={stageV2} />
                          </TableCell>
                        )}
                        {renderExpandButton(rowKey)}
                      </TableRow>
                      {renderDetailRow(
                        rowKey,
                        {
                          rateClass,
                          debits,
                          tobaccoClass,
                          product: policyProduct,
                          carrier: policyCarrier,
                          subCategory: policySubCategory,
                          term: policyTerm,
                          coverage,
                          decisionDate,
                        },
                        colSpan,
                      )}
                    </React.Fragment>
                  );
                },
              )}
            </TableBody>
          </Table>
        </TableCell>
      </TableRow>
    );
  };

  const renderPastApplicationsTable = (items: PastApplication[]) => {
    if (items.length === 0 || !canUserViewPastApplications()) return null;
    return (
      <TableRow>
        <TableCell>
          <Typography variant="h3">
            {Resource.TASK_TITLE_PAST_APPLICATIONS}
          </Typography>
          <Table>
            <TableBody>
              {items.map(policy => {
                const isBundled =
                  bundledPolicyId != null &&
                  policy.policyId === bundledPolicyId;
                const rowKey = String(policy.policyId);
                const colSpan =
                  3 +
                  (policy.detailedLifeCycleV2 ? 1 : 0) +
                  (policy.stageV2 ? 1 : 0) +
                  (isPolicyDetailsEnabled ? 1 : 0);
                return (
                  <React.Fragment key={policy.policyId}>
                    <TableRow>
                      <TableCell className={styles.PolicyTableCell}>
                        <Box
                          display="inline-block"
                          style={{
                            fontVariantNumeric: 'ordinal tabular-nums',
                          }}
                        >
                          {policy.applicationId ? (
                            <Link
                              component={RouterLink}
                              to={`/workbench/applications/${policy.applicationId}`}
                              target="_blank"
                            >
                              {policy.policyId}
                              {isBundled ? ' (bundled)' : ''}
                            </Link>
                          ) : auth.userOrganization ===
                            UserOrganization.ETHOS ? (
                            <Link
                              component={RouterLink}
                              to={`/workbench/ppwb/policy/${policy.policyId}`}
                              target="_blank"
                            >
                              {policy.policyId}
                              {isBundled ? ' (bundled)' : ''}
                            </Link>
                          ) : (
                            <Typography variant="body2">
                              {policy.policyId}
                              {isBundled ? ' (bundled)' : ''}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className={styles.PolicyTableCell}>
                        <Typography variant="body2">
                          {policy.lastRelevantDecision?.decision ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell className={styles.PolicyTableCell}>
                        <Product
                          product={policy.product}
                          carrier={policy.carrier}
                          subCategory={policy.subCategory}
                        />
                      </TableCell>
                      {policy.detailedLifeCycleV2 && (
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '10%' }}
                        >
                          <ApplicationDetailedLifecycle
                            detailedLifecycle={policy.detailedLifeCycleV2}
                          />
                        </TableCell>
                      )}
                      {policy.stageV2 && (
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '10%' }}
                        >
                          <ApplicationStageV2 stage={policy.stageV2} />
                        </TableCell>
                      )}
                      {renderExpandButton(rowKey)}
                    </TableRow>
                    {renderDetailRow(
                      rowKey,
                      {
                        rateClass: policy.lastRelevantDecision?.rateClass,
                        debits: policy.lastRelevantDecision?.debits,
                        tobaccoClass: policy.lastRelevantDecision?.tobaccoClass,
                        product: policy.product,
                        carrier: policy.carrier,
                        subCategory: policy.subCategory,
                        term: policy.term,
                        coverage: policy.coverage,
                        decisionDate: policy.lastRelevantDecision?.createdAt,
                      },
                      colSpan,
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableCell>
      </TableRow>
    );
  };

  const renderStackedPoliciesTable = () => {
    if (stackedPolicies.length === 0) return null;
    return (
      <TableRow>
        <TableCell>
          <Typography variant="h3">
            {Resource.TASK_TITLE_STACKED_POLICIES}
          </Typography>
          <Table>
            <TableBody>
              {stackedPolicies.map(
                ({
                  applicationId: stackedAppId,
                  carrierPolicyNumber,
                  product: policyProduct,
                  carrier: policyCarrier,
                  subCategory: policySubCategory,
                  status,
                  coverage,
                  term: policyTerm,
                  detailedLifeCycleV2,
                  stageV2,
                  termPolicyId,
                  lastRelevantDecision,
                }) => {
                  const rateClass = lastRelevantDecision?.rateClass;
                  const debits = lastRelevantDecision?.debits;
                  const tobaccoClass = lastRelevantDecision?.tobaccoClass;
                  const decisionDate = lastRelevantDecision?.createdAt;
                  const rowKey = carrierPolicyNumber ?? String(termPolicyId);
                  const colSpan =
                    5 +
                    (detailedLifeCycleV2 ? 1 : 0) +
                    (stageV2 ? 1 : 0) +
                    (isPolicyDetailsEnabled ? 1 : 0);
                  return (
                    <React.Fragment key={carrierPolicyNumber || termPolicyId}>
                      <TableRow>
                        <TableCell
                          className={styles.PolicyTableCell}
                          style={{ minWidth: '15%' }}
                        >
                          {stackedAppId ? (
                            <Link
                              component={RouterLink}
                              to={`/workbench/applications/${stackedAppId}`}
                              target="_blank"
                            >
                              {carrierPolicyNumber}
                            </Link>
                          ) : termPolicyId ? (
                            <Link
                              component={RouterLink}
                              to={`/workbench/ppwb/policy/${termPolicyId}`}
                              target="_blank"
                            >
                              {carrierPolicyNumber}
                            </Link>
                          ) : (
                            <Typography
                              style={{
                                fontVariantNumeric: 'ordinal tabular-nums',
                              }}
                            >
                              {carrierPolicyNumber}
                            </Typography>
                          )}
                        </TableCell>
                        {policyProduct ? (
                          <>
                            <TableCell
                              className={styles.PolicyTableCell}
                              style={{ minWidth: '30%' }}
                            >
                              <Typography variant="body2">{status}</Typography>
                            </TableCell>
                            <TableCell
                              className={styles.PolicyTableCell}
                              style={{ minWidth: '20%' }}
                            >
                              <Product
                                product={policyProduct}
                                carrier={policyCarrier}
                                subCategory={policySubCategory}
                              />
                            </TableCell>
                            <TableCell
                              className={styles.PolicyTableCell}
                              style={{ minWidth: '15%' }}
                            >
                              <Typography
                                variant="body2"
                                style={{
                                  fontVariantNumeric: 'ordinal tabular-nums',
                                }}
                              >
                                ${coverage?.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell
                              className={styles.PolicyTableCell}
                              style={{ minWidth: '10%' }}
                            >
                              {policyTerm ? (
                                <Typography variant="body2">
                                  {policyTerm} Years
                                </Typography>
                              ) : (
                                <Typography variant="body2">-</Typography>
                              )}
                            </TableCell>
                            {detailedLifeCycleV2 && (
                              <TableCell
                                className={styles.PolicyTableCell}
                                style={{ minWidth: '10%' }}
                              >
                                <ApplicationDetailedLifecycle
                                  detailedLifecycle={detailedLifeCycleV2}
                                />
                              </TableCell>
                            )}
                            {stageV2 && (
                              <TableCell
                                className={styles.PolicyTableCell}
                                style={{ minWidth: '10%' }}
                              >
                                <ApplicationStageV2 stage={stageV2} />
                              </TableCell>
                            )}
                            {renderExpandButton(rowKey)}
                          </>
                        ) : (
                          <TableCell
                            className={styles.PolicyTableCell}
                            colSpan={4}
                          >
                            <Typography variant="body2">
                              {Resource.STACKED_POLICY_NOT_FOUND_ERROR}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                      {policyProduct &&
                        renderDetailRow(
                          rowKey,
                          {
                            rateClass,
                            debits,
                            tobaccoClass,
                            product: policyProduct,
                            carrier: policyCarrier,
                            subCategory: policySubCategory,
                            term: policyTerm,
                            coverage,
                            decisionDate,
                          },
                          colSpan,
                        )}
                    </React.Fragment>
                  );
                },
              )}
            </TableBody>
          </Table>
        </TableCell>
      </TableRow>
    );
  };

  if (isOwnerInsuredSplitVariant) {
    return (
      <div
        data-label={'existing_policies'}
        data-section-id={'Existing policies'}
      >
        {hasOwnerPolicies && (
          <>
            {/* Owner's Linked Policies */}
            <Box>
              <Typography variant="h2">
                {Resource.TASK_TITLE_LINKED_POLICIES}
                &nbsp;&nbsp;
                <Typography component="span" variant="h2" fontWeight="bold">
                  Owner
                </Typography>
              </Typography>
            </Box>
            <Spacer height={16} />
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableBody>
                  {renderExistingPoliciesTable(
                    ownerPoliciesData?.activatedPolicies ?? [],
                  )}
                  {renderPastApplicationsTable(
                    ownerPoliciesData?.maestroPolicies ?? [],
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Spacer height={32} />
          </>
        )}

        {hasInsuredPolicies && (
          <>
            {/* Insured's Linked Policies */}
            <Box>
              <Typography variant="h2">
                {Resource.TASK_TITLE_LINKED_POLICIES}
                &nbsp;&nbsp;
                <Typography component="span" variant="h2" fontWeight="bold">
                  Insured
                </Typography>
              </Typography>
            </Box>
            <Spacer height={16} />
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableBody>
                  {renderStackedPoliciesTable()}
                  {renderExistingPoliciesTable(activatedPolicies)}
                  {renderPastApplicationsTable(maestroPolicies)}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>
    );
  }

  return (
    <div data-label={'existing_policies'} data-section-id={'Existing policies'}>
      <Box>
        <Typography variant="h2">
          {Resource.TASK_TITLE_LINKED_POLICIES}
        </Typography>
      </Box>
      <Spacer height={16} />
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableBody>
            {renderStackedPoliciesTable()}
            {renderExistingPoliciesTable(activatedPolicies)}
            {renderPastApplicationsTable(maestroPolicies)}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
